import { flatMap } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { AnalogModel, CheckResult, ComponentDesign } from '@paragon/contract-library';

import { SelectableItemEntry } from '../../library/selection/types';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getAnalogLinksForMember } from './getAnalogLinksForMember';
import { getAnalogModelForActiveComponentDesign } from './getAnalogModelForActiveComponentDesignSelector';
import { getCheckResultsByObjectGuid } from './getCheckResultsByObjectGuidSelectors';
import { CheckResultWithLoadCaseGuid } from './getCheckResultsForActiveAnalysisSetSelector';
import { getCheckResultsForActiveLoadCase } from './getCheckResultsForActiveLoadCaseSelector';
import { getSingleSelectedItemEntryOrNull } from './getSelectedItemSelector';

export const getCheckedObjectGuidsAssociatedWithMember = (memberGuid: string, analogModel: AnalogModel) => {
  const analogLinks = getAnalogLinksForMember(memberGuid, analogModel.analogLinks);
  const analogLinkGuids = new Set(analogLinks.map((analogLink) => analogLink.guid));
  const panels = analogModel.panels.filter((panel) => panel.linkGuids.some((guid) => analogLinkGuids.has(guid)));
  return [
    ...new Set([...panels, ...analogLinks.map((analogLink) => analogLink.memberStack)].map((o) => o.guid)),
    ...analogLinkGuids,
    memberGuid,
  ];
};

export function getCheckResultsInvolvingMember<T extends CheckResult>(
  checkResultsByObjectGuid: ReadonlyMap<string, readonly T[]>,
  memberGuid: string,
  analyzedModel: AnalogModel | null,
): readonly T[] {
  if (!analyzedModel) return [];
  const objectGuids = getCheckedObjectGuidsAssociatedWithMember(memberGuid, analyzedModel);
  return Array.from(new Set(flatMap(objectGuids, (objectGuid) => checkResultsByObjectGuid.get(objectGuid) || [])));
}

// Get check results pertaining more to this member than to other plates/members.
export function getCheckResultsForMember<T extends CheckResult>(
  checkResultsByObjectGuid: ReadonlyMap<string, readonly T[]>,
  memberGuid: string,
  analyzedModel: AnalogModel | null,
): readonly T[] {
  return getCheckResultsInvolvingMember(checkResultsByObjectGuid, memberGuid, analyzedModel).filter(
    (checkResult) => checkResult.checkCategory !== 'Plate',
  );
}

// Get the check results that should be associated with the specified item. We don't associate plate checks with members
// here, since the user will usually want to improve the plate, not change the lumber, for a failing plate check.
const getCheckResultsForItem = (
  checkResults: readonly CheckResultWithLoadCaseGuid[],
  selectedEntry: SelectableItemEntry,
  analyzedModel: AnalogModel | null,
  componentDesign: ComponentDesign | null,
): readonly CheckResultWithLoadCaseGuid[] => {
  if (selectedEntry.type === 'componentDesign' || selectedEntry.type === 'loadCase' || selectedEntry.type === 'load') {
    if (!analyzedModel) return [];
    // TODO: Fix componentGuid?
    return checkResults.filter((checkResult) =>
      checkResult.objects.find(
        (checkObject) =>
          checkObject.guid === componentDesign!.component.guid || analyzedModel.guid === checkObject.guid,
      ),
    );
  }

  if (selectedEntry.type === 'member') {
    if (!analyzedModel) return [];
    return getCheckResultsForMember(
      getCheckResultsByObjectGuid(checkResults, analyzedModel),
      selectedEntry.item.guid,
      analyzedModel,
    );
  }

  if (selectedEntry.type === 'panel') {
    // Work around a bug fixed by https://github.com/ParagonTruss/ParagonCore/pull/3480. When all old analysis results
    // have been migrated, this special treatment of panels can be removed.
    const panelName = selectedEntry.item.name;
    return checkResults.filter((checkResult) =>
      checkResult.objects.some((checkedObject) => checkedObject.type === 'Panel' && checkedObject.name === panelName),
    );
  }

  return checkResults.filter((checkResult) =>
    checkResult.objects.find((checkObject) => checkObject.guid === selectedEntry.item.guid),
  );
};

// Get the check results that should be associated with the selected load case and item. We don't associate plate checks
// with members here, since the user will usually want to improve the plate, not change the lumber, for a failing plate
// check.
export const getCheckResultsForActiveLoadCaseAndItem = createSelector(
  getCheckResultsForActiveLoadCase,
  getSingleSelectedItemEntryOrNull,
  getAnalogModelForActiveComponentDesign,
  getActiveComponentDesign,
  (
    checkResults,
    selectedEntry,
    analyzedModel,
    activeComponentDesign,
  ): readonly CheckResultWithLoadCaseGuid[] | null => {
    if (checkResults == null) return null;
    if (selectedEntry === null) return [];

    const checkResultsForSelectedItem = getCheckResultsForItem(
      checkResults,
      selectedEntry,
      analyzedModel,
      activeComponentDesign,
    );

    if (checkResultsForSelectedItem.length === 0) return [];

    return checkResultsForSelectedItem.toSorted((a, b) => b.capacity - a.capacity);
  },
);
